import { TimeFormat } from "../types";
import { timeToMinutes } from "./timeParsing";
import { isTimeOnStep, isValidTime } from "./timeValidation";
import { formatTimeString } from "./timeFormatting";

/**
 * Options for auto-rounding functionality.
 */
export interface AutoRoundingOptions {
  /** Whether auto-rounding is enabled. */
  autoround: boolean;
  /** Time increment in minutes. */
  step?: number;
  /** Time format (12 or 24 hour). */
  format: TimeFormat;
}

/**
 * Gets the next future step for a given time based on the step increment.
 *
 * @param time - The current time string
 * @param step - The step increment in minutes
 * @param format - The time format (12 or 24 hour)
 * @returns The next step time string
 */
export function getNextFutureStep(
  time: string,
  step: number,
  format: TimeFormat,
): string {
  if (!step || step <= 0) return time;

  // Convert time string to minutes
  const totalMinutes = timeToMinutes(time);

  // Find the next step that's AFTER the current time
  const nextStep = Math.ceil(totalMinutes / step) * step;

  // Convert back to string format
  const hours = Math.floor(nextStep / 60);
  const minutes = nextStep % 60;

  return formatTimeString(hours, minutes, format);
}

/**
 * Handles auto-rounding of time values based on step intervals.
 *
 * @param time - The time to potentially auto-round
 * @param options - Auto-rounding configuration options
 * @returns The rounded time or the original time if no rounding is needed
 */
export function handleAutoRounding(
  time: string | null,
  options: AutoRoundingOptions,
): string | null {
  const { autoround, step, format } = options;

  if (!autoround || !time) return time;

  // Check if the time is already on a valid step
  const stepValue = step ?? 30;

  // If the time is already on a valid step, no rounding needed
  if (isTimeOnStep(time, stepValue)) {
    return time;
  }

  // Auto-round to the next step
  return getNextFutureStep(time, stepValue, format);
}

/**
 * Generates AM/PM variants for 12-hour format auto-rounding when user hasn't specified AM/PM.
 *
 * @param currentTime - The current time string
 * @param step - Step increment in minutes
 * @param inputValue - Original user input to check for AM/PM specification
 * @returns Array of time options (AM only, PM only, or both)
 */
export function generateAmPmVariants(
  currentTime: string,
  step: number,
  inputValue: string,
): string[] {
  const hasAM = inputValue.toUpperCase().includes("AM");
  const hasPM = inputValue.toUpperCase().includes("PM");

  // Helper function to get next step for a given time
  const getNextStepForTime = (time: string) => {
    const currentMinutes = timeToMinutes(time);
    const nextStepMinutes = Math.ceil(currentMinutes / step) * step;
    const finalMinutes =
      nextStepMinutes === currentMinutes
        ? nextStepMinutes + step
        : nextStepMinutes;

    const hours = Math.floor(finalMinutes / 60);
    const minutes = finalMinutes % 60;
    return formatTimeString(hours, minutes, 12);
  };

  const nextStep = getNextFutureStep(currentTime, step, 12);

  // If user specified AM or PM, only show options in that period
  if (hasAM || hasPM) {
    return [nextStep];
  } else {
    // User didn't specify AM/PM, show both options
    // For PM version, add 12 hours to the current time
    const currentMinutes = timeToMinutes(currentTime);
    const pmMinutes = currentMinutes + 12 * 60;
    const pmHours = Math.floor(pmMinutes / 60);
    const pmMins = pmMinutes % 60;
    const pmVersion = getNextStepForTime(formatTimeString(pmHours, pmMins, 24));
    return [nextStep, pmVersion]; // Both AM and PM
  }
}

/**
 * Tries to generate PM interpretation options for ambiguous input.
 *
 * @param currentTime - The current time string
 * @param step - Step increment in minutes
 * @param inputValue - Original user input
 * @returns Array of PM time options or empty array
 */
export function generatePmInterpretation(
  currentTime: string,
  step: number,
  inputValue: string,
): string[] {
  // Only try PM interpretation if no AM/PM was specified
  if (
    inputValue.toUpperCase().includes("AM") ||
    inputValue.toUpperCase().includes("PM")
  ) {
    return [];
  }

  // Try PM version of the current time
  const currentMinutes = timeToMinutes(currentTime);
  const pmMinutes = currentMinutes + 12 * 60;
  const pmHours = Math.floor(pmMinutes / 60);
  const pmMins = pmMinutes % 60;
  const pmTime = formatTimeString(pmHours, pmMins, 12);

  // Check if the PM time is valid
  if (isValidTime(pmTime)) {
    const pmNextStep = getNextFutureStep(pmTime, step, 12);
    return [pmNextStep];
  }

  return [];
}

/**
 * Gets auto-rounding options for dropdown suggestions when no matches are found.
 *
 * @param currentTime - The current parsed time
 * @param step - Step increment in minutes
 * @param format - Time format (12 or 24 hour)
 * @param inputValue - Original user input
 * @param min - Minimum time constraint
 * @returns Array of auto-rounded time options
 */
export function getAutoRoundingOptions(
  currentTime: string | null,
  step: number,
  format: TimeFormat,
  inputValue: string,
  min?: string,
): string[] {
  if (!currentTime) {
    return [];
  }

  // Only add next future step if the current time is valid
  if (!isValidTime(currentTime)) {
    // For 12-hour format, try PM interpretation
    if (format === 12) {
      return generatePmInterpretation(currentTime, step, inputValue);
    }
    return [];
  }

  const nextStep = getNextFutureStep(currentTime, step, format);

  // For 12-hour format without time range, show AM/PM variants
  if (format === 12 && !min) {
    return generateAmPmVariants(currentTime, step, inputValue);
  }

  return [nextStep];
}
